/**
 * Unified Item Edit Modal
 * 
 * Fully self-contained adaptive modal that switches between layouts based on:
 * - Screen size (via useResponsive hook) - mobile vs desktop
 * - Edit mode (full vs guest) - all features vs simple
 * 
 * This component completely replaces MobileItemEdit, ItemEditModal, and GuestItemEditModal
 * by using shared components and layout variants.
 * 
 * Phase 3d: Fully unified implementation with no dependencies on old modals
 */

import React, { useState, useEffect } from 'react';
import { useResponsive } from '../../hooks/useResponsive';
import { UnifiedItemEditModalProps, EditableItem } from './types';
import { GuestLayout } from './layouts/GuestLayout';
import { DesktopLayout } from './layouts/DesktopLayout';
import { MobileLayout } from './layouts/MobileLayout';

export const UnifiedItemEditModal: React.FC<UnifiedItemEditModalProps> = (props) => {
  const { isMobile } = useResponsive();
  
  const {
    open,
    item,
    onClose,
    onChange,
    onSave,
    mode = 'full',
    token,
    readOnly,
    saveDisabled,
    saving = false,
  } = props;

  // Local state for the item being edited
  const [localItem, setLocalItem] = useState<EditableItem | null>(item);

  // Sync local state with props
  useEffect(() => {
    setLocalItem(item);
  }, [item]);

  // Don't render if not open or no item
  if (!open || !localItem) return null;

  // Handle changes to the item
  const handleChange = (patch: Partial<EditableItem>) => {
    setLocalItem(prev => prev ? { ...prev, ...patch } : null);
    onChange(patch);
  };

  // Handle save - just call parent's onSave
  const handleSave = async () => {
    if (!localItem?.name.trim()) {
      return;
    }
    await onSave();
  };

  // Guest mode - simple name/description editor
  if (mode === 'guest') {
    return (
      <GuestLayout
        item={localItem}
        onChange={handleChange}
        onSave={handleSave}
        onClose={onClose}
        saving={saving}
      />
    );
  }

  // Full mode - mobile vs desktop
  if (isMobile) {
    // Mobile: Full-screen edit view
    return (
      <MobileLayout
        item={localItem}
        onChange={handleChange}
        onSave={handleSave}
        onClose={onClose}
        token={token}
        saving={saving}
        saveDisabled={saveDisabled}
      />
    );
  }

  // Desktop: Modal overlay
  return (
    <DesktopLayout
      item={localItem}
      onChange={handleChange}
      onSave={handleSave}
      onClose={onClose}
      token={token}
      readOnly={readOnly}
      saveDisabled={saveDisabled}
      saving={saving}
    />
  );
};

export default UnifiedItemEditModal;
